'use client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PromotionsCard } from '@/components/pages/promotions/PromotionsPage/PromotionsCard/PromotionsCard';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { Loader } from '@/components/ui/Loader/Loader';
import { getPromotionsList } from '@/api/promotions/promotions';
import { PromotionItem } from '@/api/promotions/promotions.types';
import s from './Promotions.module.scss';
import { NoResults } from '@/components/features/NoResults/NoResults';

interface Props {
  currentPage: number;
}

const PER_PAGE = 9;

export const Promotions = ({ currentPage }: Props) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [cards, setCards] = useState<PromotionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    const fetchPromotions = async () => {
      if (searchParams.get('show_more') === 'true') return;

      setIsLoading(true);

      const { items: news, pages } = await getPromotionsList({
        page: currentPage,
        per_page: PER_PAGE,
      });

      if (!news) {
        setIsLoading(false);
        return;
      }

      setCards(news);
      setTotalPages(pages);
      setIsLoading(false);
    };

    fetchPromotions();
  }, [searchParams]);

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;

    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const { items: news } = await getPromotionsList({
      page: nextPage,
      per_page: PER_PAGE,
    });

    setCards((prev) => [...prev, ...news]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  return (
    <>
      {isLoading && <Loader fixed />}

      <section className={s.promotions}>
        <div className="custom-container">
          {cards.length > 0 ? (
            <>
              <ul className={s.promotions__list}>
                {cards.map((item) => <PromotionsCard key={item.id} card={item} />)}
              </ul>
              {currentPage < totalPages && (
                <ShowMoreButton
                  onClick={showMore}
                  disabled={totalPages < 2}
                  className={s.promotions__showMore}
                />
              )}
              <Pagination totalPages={totalPages} />
            </>
          ) : (
            <NoResults>
              <p className={s.promotions__noResults}>
                К сожалению, на данный момент у нас нет действующих специальных предложений. Но не переживайте — новые
                акции появятся совсем скоро!
              </p>
            </NoResults>
          )}
        </div>
      </section>
    </>
  );
};